import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Clock, Zap, ArrowRight, Crown, Check } from 'lucide-react';

export function TrialBanner({ daysLeft, onDismiss }) {
  const navigate = useNavigate();
  
  const urgency = daysLeft <= 2 ? 'urgent' : daysLeft <= 4 ? 'warning' : 'info';
  
  const colors = {
    info: 'bg-blue-500/10 border-blue-500/30 text-blue-700',
    warning: 'bg-orange-500/10 border-orange-500/30 text-orange-700',
    urgent: 'bg-red-500/10 border-red-500/30 text-red-700',
  };
  
  const badgeColors = {
    info: 'bg-blue-500',
    warning: 'bg-orange-500',
    urgent: 'bg-red-500',
  };

  return (
    <div className={`${colors[urgency]} border rounded-lg p-3 flex items-center gap-3`} data-testid="trial-banner">
      <div className={`${badgeColors[urgency]} rounded-md px-2 py-1 text-white text-xs font-heading font-bold uppercase shrink-0 flex items-center gap-1`}>
        <Clock className="w-3 h-3" />
        {daysLeft}d left
      </div>
      <p className="text-sm flex-1 min-w-0">
        {daysLeft <= 2
          ? 'Your free trial ends soon — keep full Contractor access: tile & metal lists, valley/ridge takeoffs, editable reports, job costing & client sharing.'
          : `Free Trial — ${daysLeft} days of full Contractor access remaining.`}
      </p>
      <Button
        onClick={() => navigate('/pricing')}
        size="sm"
        className="bg-orange-500 hover:bg-orange-400 text-white font-heading font-bold uppercase text-xs shrink-0"
        data-testid="trial-upgrade-btn"
      >
        Keep Contractor — $79/mo
      </Button>
    </div>
  );
}

export function TrialExpiredModal({ open, onClose }) {
  const navigate = useNavigate();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="trial-expired-modal">
        <DialogHeader>
          <div className="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <DialogTitle className="font-heading text-center text-xl uppercase">
            Your Free Trial Has Ended
          </DialogTitle>
          <DialogDescription className="text-center">
            Keep the full Contractor toolkit you've been using for $79/month. No interruptions.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-2 my-4">
          {[
            'Shingle, tile & metal material lists',
            'Valley & ridge takeoffs',
            'Editable reports & custom materials',
            'Job cost estimator',
            'Shareable, white-label client reports',
            'CSV export & team members',
          ].map((feat, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-slate-700">
              <Check className="w-4 h-4 text-green-500 shrink-0" />
              {feat}
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2 mt-4">
          <Button
            onClick={() => { onClose(false); navigate('/pricing'); }}
            className="bg-orange-500 hover:bg-orange-400 text-white h-12 font-heading font-bold uppercase tracking-wide"
            data-testid="trial-expired-upgrade-btn"
          >
            Keep Contractor — $79/mo
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <Button
            onClick={() => onClose(false)}
            variant="ghost"
            className="text-slate-500"
          >
            Continue with Free (3 measurements/month)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TrialNavBadge({ daysLeft }) {
  const navigate = useNavigate();
  const urgency = daysLeft <= 2 ? 'bg-red-500' : daysLeft <= 4 ? 'bg-orange-500' : 'bg-blue-500';
  
  return (
    <button
      onClick={() => navigate('/pricing')}
      className={`${urgency} text-white text-xs font-heading font-bold uppercase px-2.5 py-1 rounded-full flex items-center gap-1 hover:opacity-90 transition-opacity`}
      data-testid="trial-nav-badge"
    >
      <Zap className="w-3 h-3" />
      <span className="hidden sm:inline">Free Trial</span> {daysLeft}d
    </button>
  );
}
